import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { get } from '@/lib/api';
import { storeTemplateHref, templateEditorHref } from '@/lib/template-editor-query';
import { isSportArtworkTemplate } from '@/lib/store-template-kinds';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

type StoreTemplate = {
  id: string;
  name?: string;
  description?: string;
  brands?: string[];
  styleCount?: number;
  styles?: unknown[];
  categories?: string[];
  brandTag?: string;
  active?: boolean;
};
type SportTemplate = {
  id: string;
  name?: string;
  sport?: string;
  icon?: string;
  description?: string;
  textTreatment?: string;
  fontFamily?: string;
  active?: boolean;
};

export default function Templates() {
  const { data: storeData, isLoading: storeLoading } = useSWR<{ templates: StoreTemplate[] }>('/api/store-templates', get);
  const { data: sportData, isLoading: sportLoading } = useSWR<{ templates: SportTemplate[] }>('/api/sport-templates', get);

  return (
    <div className="px-7 py-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Template Library</h1>
          <p className="text-[12px] text-muted">
            Store templates ({storeData?.templates.length ?? 0}) and sport templates ({sportData?.templates.length ?? 0}). Open a card to edit it.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Link
            to="/product-template-editor"
            className="rounded-[8px] border border-black/[0.08] bg-surface px-3 py-1.5 text-[11.5px] font-medium text-text shadow-[0_1px_1.5px_rgba(0,0,0,0.04)] hover:bg-[#f7f7f9]"
          >
            Product lineups →
          </Link>
          <Link
            to="/store-template-editor"
            className="rounded-[8px] border border-black/[0.08] bg-surface px-3 py-1.5 text-[11.5px] font-medium text-text shadow-[0_1px_1.5px_rgba(0,0,0,0.04)] hover:bg-[#f7f7f9]"
          >
            Assign designs →
          </Link>
        </div>
      </div>

      <h2 className="mb-2 text-[13px] font-semibold">Store Templates</h2>
      <div className="mb-6 grid gap-2 md:grid-cols-2">
        {storeLoading ? (
          <>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </>
        ) : storeData?.templates.length ? (
          storeData.templates.map((t) => (
            <Link
              key={t.id}
              to={storeTemplateHref(t.id)}
              className="rounded-md bg-surface p-4 shadow-card hover:bg-[#f7f7f9]"
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-subtle">{t.id}</span>
                {t.active === false && <Badge className="bg-bg text-muted">inactive</Badge>}
                {t.brandTag && <Badge className="bg-primary/10 text-primary">{t.brandTag}</Badge>}
              </div>
              <h3 className="mt-1 text-[13px] font-semibold">{t.name || 'Untitled'}</h3>
              {t.description && <p className="mt-1 text-[11px] text-muted">{t.description}</p>}
              <div className="mt-2 flex gap-3 text-[10.5px] text-muted">
                {t.styleCount != null && <span>{t.styleCount} styles</span>}
                {t.brands?.length ? <span>{t.brands.join(', ')}</span> : null}
                {t.categories?.length ? <span>{t.categories.join(' · ')}</span> : null}
              </div>
              <div className="mt-2 text-[11px] text-primary underline">
                {isSportArtworkTemplate(t) ? 'Connect garments & designs →' : 'Edit product lineup →'}
              </div>
            </Link>
          ))
        ) : (
          <div className="col-span-2 rounded-md bg-surface p-6 text-center text-[12px] text-muted shadow-card">No store templates.</div>
        )}
      </div>

      <h2 className="mb-2 text-[13px] font-semibold">Sport Templates</h2>
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {sportLoading ? (
          <>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </>
        ) : sportData?.templates.length ? (
          sportData.templates.map((t) => (
            <Link
              key={t.id}
              to={templateEditorHref({ templateId: t.id.startsWith('sport-') ? t.id : `sport-${t.id}` })}
              className="rounded-md bg-surface p-4 shadow-card hover:bg-[#f7f7f9]"
            >
              <div className="flex items-center gap-2">
                {t.icon && <span className="text-[16px]">{t.icon}</span>}
                <span className="font-mono text-[10px] text-subtle">{t.id}</span>
                {t.active === false && <Badge className="bg-bg text-muted">inactive</Badge>}
              </div>
              <h3 className="mt-1 text-[13px] font-semibold">{t.name || 'Untitled'}</h3>
              <div className="mt-1 flex gap-2 text-[11px]">
                {t.sport && <Badge className="bg-success/10 text-success">{t.sport}</Badge>}
                {t.textTreatment && <Badge className="bg-bg text-muted">{t.textTreatment}</Badge>}
              </div>
              {t.description && <p className="mt-2 text-[11px] text-muted">{t.description}</p>}
              {t.fontFamily && <p className="mt-1 text-[10px] text-subtle">Font: {t.fontFamily}</p>}
              <div className="mt-2 text-[11px] text-primary underline">Assign designs →</div>
            </Link>
          ))
        ) : (
          <div className="col-span-3 rounded-md bg-surface p-6 text-center text-[12px] text-muted shadow-card">No sport templates.</div>
        )}
      </div>
    </div>
  );
}
